
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BlogPost } from "@/types/blog";
import { FileJson } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { readFileAsText, processWordPressJsonPosts } from "@/utils/wordpress/jsonProcessor";

interface JsonFileUploaderProps {
  onImportComplete: (posts: BlogPost[]) => void;
}

export const JsonFileUploader = ({ onImportComplete }: JsonFileUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    toast({
      title: "Processing JSON File",
      description: "Parsing JSON and processing images. This may take a moment...",
    });
    
    try {
      // Read the file contents
      const content = await readFileAsText(file);
      
      // Parse the JSON content
      let parsedData;
      try {
        parsedData = JSON.parse(content);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        throw new Error(`Invalid JSON format: ${parseError instanceof Error ? parseError.message : "Could not parse file"}`);
      }
      
      // Process the data into BlogPost format
      let posts: BlogPost[] = [];
      
      try {
        console.log("Starting WordPress JSON processing...");
        if (Array.isArray(parsedData)) {
          // If it's an array, assume it's an array of posts
          console.log(`Processing array of ${parsedData.length} posts`);
          posts = await processWordPressJsonPosts(parsedData);
        } else if (parsedData && parsedData.posts && Array.isArray(parsedData.posts)) {
          // WordPress export format may have a posts array within the JSON
          console.log(`Processing ${parsedData.posts.length} posts from posts array property`);
          posts = await processWordPressJsonPosts(parsedData.posts);
        } else if (parsedData) {
          // Single post object, try to process it
          console.log("Processing single post object");
          const singlePost = await processWordPressJsonPosts([parsedData]);
          if (singlePost && singlePost.length > 0) {
            posts = singlePost;
          }
        }
        
        console.log(`Processed ${posts.length} posts successfully`);
      } catch (processingError) {
        console.error("Error processing posts:", processingError);
        throw new Error(`Failed to process posts: ${processingError instanceof Error ? processingError.message : "Unknown error"}`);
      }
      
      if (posts.length > 0) {
        toast({
          title: "JSON Processed Successfully",
          description: `Found ${posts.length} posts in the JSON file`,
        });
        
        // Log a sample post for debugging
        if (posts[0]) {
          console.log("Sample processed post:", {
            title: posts[0].title,
            coverImage: posts[0].coverImage,
            tags: posts[0].tags,
          });
        }
        
        onImportComplete(posts);
      } else {
        toast({
          title: "No Posts Found",
          description: "Could not find any valid posts in the JSON file",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error processing JSON file:", error);
      toast({
        title: "Error Processing JSON",
        description: error instanceof Error ? error.message : "Invalid JSON format",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset the input file
      event.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        type="file"
        accept=".json"
        onChange={handleFileUpload}
        disabled={isUploading}
        className="flex-1 hidden"
        id="json-file-input"
      />
      <Button
        type="button"
        variant="outline"
        disabled={isUploading}
        onClick={() => document.getElementById('json-file-input')?.click()}
        className="w-full flex items-center justify-center gap-2"
      >
        {isUploading ? (
          <>Processing...</>
        ) : (
          <>
            <FileJson size={16} />
            Upload WordPress JSON
          </>
        )}
      </Button>
    </div>
  );
};
